import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Heart, MessageCircle, Share2, Bookmark,
    Play, Music, MoreHorizontal, User,
    Camera, Sparkles, ChevronLeft, ChevronRight, Check
} from 'lucide-react';

// Template definitions for Coming Soon carousel
const COMING_SOON_TEMPLATES = [
    {
        id: 'minimal-premiere',
        name: 'Minimal Premiere',
        description: 'Clean, sophisticated design',
        image: '/repurpose/coming_soon_template/Minimal_Premiere.png',
        gradient: 'from-gray-100 to-gray-200'
    },
    {
        id: 'cinematic-dark',
        name: 'Cinematic Dark',
        description: 'Bold, dramatic aesthetic',
        image: '/repurpose/coming_soon_template/Cinematic_Dark.png',
        gradient: 'from-gray-900 to-black'
    },
    {
        id: 'modern-editorial',
        name: 'Modern Editorial',
        description: 'Magazine-inspired design',
        image: '/repurpose/coming_soon_template/Modern_Editorial.png',
        gradient: 'from-white to-gray-100'
    },
    {
        id: 'geometric-pop',
        name: 'Geometric Pop',
        description: 'Vibrant, energetic design',
        image: '/repurpose/coming_soon_template/Geometric_Pop.png',
        gradient: 'from-purple-400 to-pink-500'
    },
    {
        id: 'expressive-wave',
        name: 'Expressive Wave',
        description: 'Artistic, fluid design',
        image: '/repurpose/coming_soon_template/Expressive_Wave.png',
        gradient: 'from-blue-400 to-cyan-300'
    }
];

// Helper to determine Instagram content subtype
export const getInstagramContentType = (contentType) => {
    if (!contentType) return null;

    const id = contentType.id?.toLowerCase() || '';
    const name = contentType.name?.toLowerCase() || '';

    // Reels content types
    if (id.includes('reels') || name.includes('reels')) {
        return 'reels';
    }

    // Stories content types
    if (id.includes('stories') || name.includes('stories')) {
        return 'stories';
    }

    // Coming Soon content types
    if (id.includes('coming-soon') || name.includes('coming soon')) {
        return 'coming-soon';
    }

    // Quote Card content types
    if (id.includes('quote-card') || name.includes('quote')) {
        return 'quote-card';
    }

    // Post/Carousel content types
    if (id.includes('carousel') || name.includes('carousel') ||
        id.includes('post') || name.includes('post') ||
        id.includes('guest-spotlight') || name.includes('spotlight')) {
        return 'post';
    }

    // Default for Instagram platform
    if (contentType.platform === 'Instagram') {
        return 'post';
    }

    return null;
};

// Instagram Reels Preview Component (Full-screen vertical video style, no phone frame)
// Matches authentic Instagram Reels UI with proper dimensions for container
export const InstagramReelsPreview = () => {
    return (
        <div className="relative w-full h-full bg-black overflow-hidden flex flex-col font-sans">
            {/* Video Background Placeholder */}
            <div className="absolute inset-0 bg-gradient-to-b from-gray-800 via-gray-900 to-black flex items-center justify-center">
                {/* Abstract Shapes for Video approximation */}
                <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-600/20 rounded-full blur-[80px] animate-pulse" />
                <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-pink-600/20 rounded-full blur-[80px] animate-pulse" style={{ animationDelay: '1s' }} />

                <div className="relative w-[15%] max-w-[64px] aspect-square rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center z-10 border border-white/20">
                    <Play className="w-[50%] h-auto text-white ml-1" fill="white" />
                </div>
            </div>

            {/* Content Overlay */}
            <div className="absolute inset-0 z-10 flex flex-col justify-between p-4 bg-gradient-to-b from-black/30 via-transparent to-black/90">
                {/* Top Area */}
                <div className="flex justify-between items-center pt-2 px-1">
                    <span className="text-[2.5vh] font-bold tracking-tight text-white drop-shadow-md">Reels</span>
                    <Camera className="w-[7%] max-w-[26px] h-auto text-white drop-shadow-md" strokeWidth={2} />
                </div>

                {/* Main Interaction Area */}
                <div className="flex flex-col flex-1 justify-end pb-4">
                    <div className="flex items-end gap-4">
                        {/* Bottom Left Info */}
                        <div className="flex-1 space-y-4 pb-1">
                            {/* User Profile */}
                            <div className="flex items-center gap-3">
                                <div className="relative">
                                    <div className="w-[10vw] max-w-[40px] aspect-square rounded-full bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-600 p-[2px]">
                                        <div className="w-full h-full rounded-full bg-black flex items-center justify-center overflow-hidden border border-black">
                                            <User className="w-[50%] h-auto text-white" />
                                        </div>
                                    </div>
                                    <div className="absolute -bottom-1 -right-1 bg-blue-500 rounded-full w-4 h-4 flex items-center justify-center border border-black">
                                        <div className="w-2 h-0.5 bg-white rounded-full" />
                                        <div className="w-0.5 h-2 bg-white rounded-full absolute" />
                                    </div>
                                </div>
                                <span className="text-xs font-semibold text-white drop-shadow-md">podguestly</span>
                                <button className="px-3 py-1 bg-transparent border border-white/60 rounded-lg text-[11px] font-semibold text-white backdrop-blur-sm hover:bg-white/10 transition-colors">
                                    Follow
                                </button>
                            </div>

                            {/* Caption */}
                            <div className="space-y-2">
                                <p className="text-xs text-white/95 leading-snug line-clamp-2 drop-shadow-sm font-normal">
                                    EP 42: Building the future with AI <span className="text-white/80">#podcast #ai #tech</span>
                                </p>
                            </div>

                            {/* Audio */}
                            <div className="flex items-center gap-2 px-3 py-[0.5vh] bg-white/20 rounded-full w-fit backdrop-blur-md border border-white/10">
                                <div className="flex items-center gap-[0.5vw] overflow-hidden max-w-[20vw]">
                                    <Music className="w-[3%] min-w-[12px] h-auto text-white flex-shrink-0" />
                                    <span className="text-[1.2vh] text-white whitespace-nowrap animate-marquee">Original Audio · PodGuestly</span>
                                </div>
                            </div>
                        </div>

                        {/* Right Side Actions */}
                        <div className="flex flex-col items-center gap-[2vh] pb-2 w-[15%]">
                            <div className="flex flex-col items-center gap-1 w-full">
                                <Heart className="w-[60%] h-auto text-white drop-shadow-md" strokeWidth={2} />
                                <span className="text-[10px] font-medium text-white drop-shadow-md">24.5K</span>
                            </div>
                            <div className="flex flex-col items-center gap-1 w-full">
                                <MessageCircle className="w-[60%] h-auto text-white drop-shadow-md" strokeWidth={2} />
                                <span className="text-[10px] font-medium text-white drop-shadow-md">1.2K</span>
                            </div>
                            <div className="flex flex-col items-center gap-1 w-full">
                                <Share2 className="w-[55%] h-auto text-white drop-shadow-md" strokeWidth={2} />
                                <span className="text-[10px] font-medium text-white drop-shadow-md">Share</span>
                            </div>
                            <div className="flex flex-col items-center gap-1 w-full">
                                <MoreHorizontal className="w-[50%] h-auto text-white drop-shadow-md" />
                            </div>
                            <div className="mt-2 w-[55%] aspect-square rounded-md border-2 border-white/80 overflow-hidden shadow-lg animate-spin" style={{ animationDuration: '4s' }}>
                                <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                                    <div className="w-3 h-3 bg-gradient-to-tr from-pink-500 to-purple-500 rounded-full" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Progress Bar */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20 z-20">
                <div className="h-full w-[45%] bg-white/90" />
            </div>
        </div>
    );
};

// Instagram Coming Soon Post Preview with Template Carousel
export const InstagramComingSoonPreview = ({
    posterUrl,
    guestName,
    topic,
    selectedTemplate,
    onTemplateSelect,
    generatedCaption
}) => {
    const [currentTemplateIndex, setCurrentTemplateIndex] = useState(0);
    const [imageLoaded, setImageLoaded] = useState({});
    const [imageError, setImageError] = useState({});
    const [isSelected, setIsSelected] = useState(false);

    // Reset image states when template changes
    useEffect(() => {
        setImageLoaded({});
        setImageError({});
    }, []);

    // Reset selection when template changes via navigation
    useEffect(() => {
        setIsSelected(false);
    }, [currentTemplateIndex]);

    const currentTemplate = COMING_SOON_TEMPLATES[currentTemplateIndex];

    const goToPrevious = useCallback(() => {
        setCurrentTemplateIndex((prev) =>
            prev === 0 ? COMING_SOON_TEMPLATES.length - 1 : prev - 1
        );
    }, []);

    const goToNext = useCallback(() => {
        setCurrentTemplateIndex((prev) =>
            prev === COMING_SOON_TEMPLATES.length - 1 ? 0 : prev + 1
        );
    }, []);

    const handleSelect = useCallback(() => {
        setIsSelected(true);
        if (onTemplateSelect) {
            onTemplateSelect(currentTemplate);
        }
    }, [currentTemplate, onTemplateSelect]);

    const handleImageLoad = useCallback((index) => {
        setImageLoaded(prev => ({ ...prev, [index]: true }));
    }, []);

    const handleImageError = useCallback((index) => {
        setImageError(prev => ({ ...prev, [index]: true }));
    }, []);

    return (
        <div className="flex flex-col h-full bg-white overflow-hidden font-sans">
            {/* Post Header */}
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 flex-shrink-0">
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-600 p-[2px]">
                            <div className="w-full h-full rounded-full bg-white flex items-center justify-center p-[2px] overflow-hidden">
                                <User size={16} className="text-gray-400 bg-gray-100 w-full h-full p-0.5" />
                            </div>
                        </div>
                    </div>
                    <div>
                        <div className="flex items-center gap-1">
                            <span className="text-xs font-semibold text-gray-900 leading-none">podguestly</span>
                            <div className="w-0.5 h-0.5 rounded-full bg-gray-500" />
                            <span className="text-[10px] font-semibold text-blue-500 leading-none">Follow</span>
                        </div>
                        <p className="text-[10px] text-gray-500 leading-none mt-0.5">Original Audio</p>
                    </div>
                </div>
                <MoreHorizontal size={20} className="text-gray-900" />
            </div>

            {/* Image/Carousel Area - Square Aspect Ratio */}
            <div className="relative aspect-square w-full bg-gray-100 flex-shrink-0 overflow-hidden">
                {posterUrl ? (
                    // Display generated poster
                    <img
                        src={posterUrl}
                        alt="Coming Soon Poster"
                        className="w-full h-full object-cover"
                    />
                ) : (
                    // Template Carousel
                    <>
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={currentTemplateIndex}
                                initial={{ opacity: 0, x: 100 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -100 }}
                                transition={{ duration: 0.3 }}
                                className="w-full h-full relative"
                            >
                                {/* Fallback gradient - shown while loading or on error */}
                                <div className={`absolute inset-0 bg-gradient-to-br ${currentTemplate.gradient} flex items-center justify-center`}>
                                    <div className="text-center p-6">
                                        <Sparkles className="w-10 h-10 text-white/80 mx-auto mb-3" />
                                        <h3 className="text-2xl font-bold text-white/90">COMING SOON</h3>
                                        <p className="text-sm text-white/70 mt-1">{currentTemplate.name}</p>
                                    </div>
                                </div>

                                {/* Template Image - loads on top of fallback */}
                                <img
                                    src={currentTemplate.image}
                                    alt={currentTemplate.name}
                                    className={`w-full h-full object-cover relative z-10 transition-opacity duration-300 ${imageLoaded[currentTemplateIndex] ? 'opacity-100' : 'opacity-0'
                                        }`}
                                    onLoad={() => handleImageLoad(currentTemplateIndex)}
                                    onError={() => handleImageError(currentTemplateIndex)}
                                />

                                {/* Template Name Overlay */}
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3 z-20">
                                    <p className="text-white text-xs font-medium">{currentTemplate.name}</p>
                                    <p className="text-white/70 text-[10px]">{currentTemplate.description}</p>
                                </div>
                            </motion.div>
                        </AnimatePresence>

                        {/* Carousel Indicators */}
                        <div className="absolute top-3 right-3 flex gap-1 z-20">
                            {COMING_SOON_TEMPLATES.map((_, idx) => (
                                <div
                                    key={idx}
                                    className={`w-1.5 h-1.5 rounded-full transition-colors ${idx === currentTemplateIndex ? 'bg-white' : 'bg-white/40'
                                        }`}
                                />
                            ))}
                        </div>

                        {/* Template Counter */}
                        <div className="absolute top-3 left-3 bg-black/40 backdrop-blur-sm px-2 py-1 rounded-full z-20">
                            <span className="text-[10px] font-medium text-white">
                                {currentTemplateIndex + 1}/{COMING_SOON_TEMPLATES.length}
                            </span>
                        </div>
                    </>
                )}
            </div>

            {/* Carousel Navigation - Outside Image Container */}
            {!posterUrl && (
                <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-100">
                    <button
                        onClick={goToPrevious}
                        className="w-8 h-8 bg-white hover:bg-gray-100 border border-gray-200 rounded-full flex items-center justify-center transition-colors shadow-sm"
                    >
                        <ChevronLeft className="w-4 h-4 text-gray-600" />
                    </button>

                    {/* Select Button with Selected State */}
                    <motion.button
                        onClick={handleSelect}
                        disabled={isSelected}
                        className={`px-4 py-1.5 text-xs font-semibold rounded-full shadow-md transition-all flex items-center gap-1.5 ${isSelected
                            ? 'bg-green-500 text-white cursor-default'
                            : 'bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white'
                            }`}
                        whileTap={!isSelected ? { scale: 0.95 } : {}}
                        initial={false}
                        animate={isSelected ? { scale: [1, 1.05, 1] } : {}}
                        transition={{ duration: 0.3 }}
                    >
                        {isSelected ? (
                            <>
                                <Check className="w-3.5 h-3.5" />
                                <span>Selected</span>
                            </>
                        ) : (
                            <span>Select Style</span>
                        )}
                    </motion.button>

                    <button
                        onClick={goToNext}
                        className="w-8 h-8 bg-white hover:bg-gray-100 border border-gray-200 rounded-full flex items-center justify-center transition-colors shadow-sm"
                    >
                        <ChevronRight className="w-4 h-4 text-gray-600" />
                    </button>
                </div>
            )}

            {/* Action Buttons Row */}
            <div className="px-3 pt-3 pb-1 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-4">
                    <Heart size={24} className="text-gray-900 stroke-[1.5] hover:text-red-500 transition-colors cursor-pointer" />
                    <MessageCircle size={24} className="text-gray-900 stroke-[1.5] -rotate-90" />
                    <Share2 size={24} className="text-gray-900 stroke-[1.5]" />
                </div>
                <Bookmark size={24} className="text-gray-900 stroke-[1.5]" />
            </div>

            {/* Likes */}
            <div className="px-3 pb-1 flex-shrink-0">
                <p className="text-sm font-semibold text-gray-900">1,247 likes</p>
            </div>

            {/* Caption Area - Scrollable if needed */}
            <div className="px-3 pb-2 flex-1 overflow-y-auto scrollbar-hide min-h-0">
                <div className="text-sm text-gray-900 leading-normal">
                    <span className="font-semibold mr-1.5">podguestly</span>
                    {generatedCaption ? (
                        generatedCaption
                    ) : guestName ? (
                        <>
                            A new episode with {guestName} is coming soon! <span className="text-gray-600">Stay tuned for insights on {topic || 'exciting topics'}.</span>
                            <br /><br />
                            <span className="text-blue-900">#podcasting #comingsoon #teaser #newepisode</span>
                        </>
                    ) : (
                        <>
                            A new episode is on the way! <span className="text-gray-600">We're preparing something special for you.</span>
                            <br /><br />
                            <span className="text-blue-900">#podcasting #comingsoon #teaser #newepisode</span>
                        </>
                    )}
                </div>
                <p className="text-xs text-gray-500 mt-2 font-medium cursor-pointer">View all 12 comments</p>
                <p className="text-[10px] text-gray-400 mt-1 uppercase tracking-wide">Just now</p>
            </div>
        </div>
    );
};

// Instagram Post/Carousel Preview Component (no phone frame)
export const InstagramPostPreview = () => {
    return (
        <div className="flex flex-col h-full bg-white overflow-hidden font-sans">
            {/* Post Header */}
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 flex-shrink-0">
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-600 p-[2px]">
                            <div className="w-full h-full rounded-full bg-white flex items-center justify-center p-[2px] overflow-hidden">
                                <User size={16} className="text-gray-400 bg-gray-100 w-full h-full p-0.5" />
                            </div>
                        </div>
                    </div>
                    <div>
                        <div className="flex items-center gap-1">
                            <span className="text-xs font-semibold text-gray-900 leading-none">podguestly</span>
                            <div className="w-0.5 h-0.5 rounded-full bg-gray-500" />
                            <span className="text-[10px] font-semibold text-blue-500 leading-none">Follow</span>
                        </div>
                        <p className="text-[10px] text-gray-500 leading-none mt-0.5">Original Audio</p>
                    </div>
                </div>
                <MoreHorizontal size={20} className="text-gray-900" />
            </div>

            {/* Image/Carousel Area - Square Aspect Ratio */}
            <div className="relative aspect-square w-full bg-gray-100 flex-shrink-0 group overflow-hidden">
                {/* Simulated Content */}
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center">
                    <div className="text-center px-8">
                        <div className="mx-auto w-12 h-12 mb-3 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center">
                            <Sparkles size={24} />
                        </div>
                        <h3 className="font-serif text-xl text-gray-900 font-medium leading-tight mb-2">The Future of AI in Podcasting</h3>
                        <p className="text-xs text-gray-500 leading-relaxed max-w-[200px] mx-auto">Discover how AI is transforming content creation workflows in our latest episode.</p>
                    </div>
                </div>

                {/* Carousel Indicator (Simulated) */}
                <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-md px-2 py-1 rounded-full text-[10px] font-semibold text-white">
                    1/5
                </div>

                {/* Pagination Dots */}
                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                    <div className="w-1 h-1 rounded-full bg-gray-300" />
                    <div className="w-1 h-1 rounded-full bg-gray-300" />
                    <div className="w-1 h-1 rounded-full bg-gray-300" />
                </div>
            </div>

            {/* Action Buttons Row */}
            <div className="px-3 pt-3 pb-1 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-4">
                    <Heart size={24} className="text-gray-900 stroke-[1.5] hover:text-red-500 transition-colors cursor-pointer" />
                    <MessageCircle size={24} className="text-gray-900 stroke-[1.5] -rotate-90" />
                    <Share2 size={24} className="text-gray-900 stroke-[1.5]" />
                </div>
                <Bookmark size={24} className="text-gray-900 stroke-[1.5]" />
            </div>

            {/* Likes */}
            <div className="px-3 pb-1 flex-shrink-0">
                <p className="text-sm font-semibold text-gray-900">1,247 likes</p>
            </div>

            {/* Caption Area - Scrollable if needed */}
            <div className="px-3 pb-2 flex-1 overflow-y-auto scrollbar-hide min-h-0">
                <div className="text-sm text-gray-900 leading-normal">
                    <span className="font-semibold mr-1.5">podguestly</span>
                    We're excited to announce our latest features! <span className="text-gray-600">AI-powered repurposing is here to change the game. Turn your long-form episodes into viral clips in seconds.</span>
                    <br /><br />
                    <span className="text-blue-900">#podcasting #ai #contentcreation #startup #growth</span>
                </div>
                <p className="text-xs text-gray-500 mt-2 font-medium cursor-pointer">View all 48 comments</p>
                <p className="text-[10px] text-gray-400 mt-1 uppercase tracking-wide">2 hours ago</p>
            </div>
        </div>
    );
};

// Legacy Instagram Preview Component (for backward compatibility)
export const InstagramPreview = () => {
    return <InstagramPostPreview />;
};

// Export a helper hook for Instagram content detection
export const useInstagramContent = (contentType, activePlatform) => {
    const instagramContentType = getInstagramContentType(contentType);

    const isInstagramReels = instagramContentType === 'reels';

    const isInstagramContent = contentType?.platform === 'Instagram' ||
        activePlatform === 'instagram' ||
        instagramContentType !== null;

    return {
        instagramContentType,
        isInstagramReels,
        isInstagramContent
    };
};

// Export templates for use in other components
export { COMING_SOON_TEMPLATES };

export default {
    getInstagramContentType,
    InstagramReelsPreview,
    InstagramComingSoonPreview,
    InstagramPostPreview,
    InstagramPreview,
    useInstagramContent,
    COMING_SOON_TEMPLATES
};
